import { useMemo, useCallback } from 'react';
import { useGetMappingLocationsQuery } from '@/entities/dashboard/mapping';
import { LocationModel, LocationService } from '@/entities/location';
import { useProjectSiteParams } from '@/shared/lib';
import type { LocationType } from '@/entities/dashboard/mapping';

export function useMapLocations() {
  const { projectId, siteId } = useProjectSiteParams();
  const { data, isLoading, error } = useGetMappingLocationsQuery({ projectId, siteId });

  const locationService = useMemo(() => {
    const service = new LocationService();
    if (data) {
      service.loadFromResponse(data);
    }
    return service;
  }, [data]);

  const locations = useMemo(() => {
    return locationService.getAll();
  }, [locationService]);

  const locationsByType = useMemo(() => {
    return locationService.groupByType();
  }, [locationService]);

  const statistics = useMemo(() => {
    return locationService.getStatistics();
  }, [locationService]);

  const searchLocations = useCallback(
    (query: string) => {
      return locationService.searchLocations(query);
    },
    [locationService]
  );

  const findNearestLocation = useCallback(
    (lat: number, lng: number, type?: LocationType) => {
      return locationService.findNearestLocation(lat, lng, type);
    },
    [locationService]
  );

  const getLocationsByDistance = useCallback(
    (fromLocation: LocationModel, maxDistance: number, type?: LocationType) => {
      return locationService.getLocationsByDistance(fromLocation, maxDistance, type);
    },
    [locationService]
  );

  return {
    data,
    locations,
    locationsByType,
    statistics,
    isLoading,
    error,
    searchLocations,
    findNearestLocation,
    getLocationsByDistance,
    getSiteName: (siteId: string) => locationService.getSiteName(siteId),
    getRegionName: (regionId: string) => locationService.getRegionName(regionId),
    getSubregionName: (subregionId: string) => locationService.getSubregionName(subregionId),
    getCountryName: (countryId: string) => locationService.getCountryName(countryId)
  };
}
